import { useEffect, useLayoutEffect, useState } from 'react';
import { useAuth, ShellAuthProvider } from '@aimsos/auth';
import { WidgetsApp } from '@aimsos/dashboards';
import { registerNamespace, i18n } from '../i18n/bootstrap.js';

/**
 * Mounts the Widget Library / Widget Playground surface inside the Core Shell.
 * Mirrors DashboardsEmbed exactly — same app package, same `dashboards` i18n
 * namespace — but mounts the `widgets` domain root (CT-5183). Two embeds rather
 * than one with a prop, because both are mounted under a splat and cannot tell
 * themselves apart from the remaining path.
 *
 * Reads from the unified useAuth() (backed by our own backend JWT) instead
 * of useAuth0() directly, so this still mounts correctly on a
 * {slug}.{rootDomain} subdomain landed on via a cross-subdomain handoff,
 * where Auth0 SDK itself has no session.
 */
const WidgetsEmbed = () => {
  const { user, token, isAuthenticated, getAccessTokenSilently } = useAuth();
  const [i18nReady, setI18nReady] = useState(false);

  useEffect(() => {
    let cancelled = false;
    import('@aimsos/dashboards/i18n/register')
      .then(({ registerDashboardsI18n }) => {
        registerDashboardsI18n(registerNamespace);
        return i18n.loadNamespaces('dashboards');
      })
      .then(() => {
        if (!cancelled) setI18nReady(true);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  useLayoutEffect(() => {
    if (!isAuthenticated) return;
    window.__AIMS_SHELL__ = {
      mounted: true,
      // The Dashboard API is its own gateway, not the platform one — the
      // widget module lives behind `{stage}-aimsos-dashboard-api`. Injected
      // the same way Data Studio and Governance get theirs, because an
      // embedded app cannot read the shell's `import.meta.env`.
      apiBaseUrlDashboard: import.meta.env.VITE_API_BASE_URL_DASHBOARD,
      // The Widget Builder's entity and field pickers are served by the
      // data-model aggregate, which lives on the Data Studio gateway.
      apiBaseUrlDS: import.meta.env.VITE_API_BASE_URL_DS,
    };
    return () => {
      delete window.__AIMS_SHELL__;
    };
  }, [isAuthenticated]);

  if (!isAuthenticated || !token || !i18nReady) return null;

  return (
    <ShellAuthProvider
      token={token}
      user={user}
      isAuthenticated={isAuthenticated}
      getAccessTokenSilently={getAccessTokenSilently}
    >
      <WidgetsApp />
    </ShellAuthProvider>
  );
};

export default WidgetsEmbed;
